import ArrowForwardColoredIcon from "assets/svgComponents/PresaleBlock/ArrowForwardColoredIcon";
import { ConnectWallet } from "assets/svgComponents/PresaleBlock/ConnectWallet";
import { CheckmarkIcon } from "assets/svgComponents/buyTokenForm/CheckIcon";
import ChevronBack from "assets/svgComponents/buyTokenForm/ChevronBack";
import clsx from "clsx";
import { Loader } from "components/Loader";
import { NetworkKey, PaymentMethod, networkData } from "constants/methods";
import { useCopyToClipboard } from "hooks/useCopyToClipboard";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import QRCode from "react-qrcode-logo";
import { toast } from "react-toastify";
import {
  gradientRowBgClass,
  investButtonClass,
  referralCtaButtonClass,
  shadowAndBorderClass,
} from "screens/Dashboard/utils/classes";
import { AnimatedGradientBorder } from "screens/Landing/components/shared";
import { authApi } from "services/auth";
import { tokensApi } from "services/tokens";
import { appStore } from "store";
import { Button } from "ui-kit/Button";
import { calcAmountOfTokensToShow, truncateMiddle } from "utils";
import { useAccount } from "wagmi";
import attsLogo from "../../../../assets/img/attsLogo.png";
import TransactionStatusChecker from "./CheckingTransactionLoader";
import { PaymentResultModal } from "./PaymentResultModal";
import { PaymentFormType, Step } from "./scheme";
import { Copy } from "lucide-react";

interface SendDepositProps {
  paymentInUSD: number;
  preselectedMethod: PaymentMethod;
  setIsPaymentDialogOpen: Dispatch<SetStateAction<boolean>>;
  referralCode: string;
  handleChangeStep: (step: Step) => void;
  setActualPaymentAmount: Dispatch<SetStateAction<number>>;
  actualPaymentAmount: number;
  setIsSucces: Dispatch<SetStateAction<boolean>>;
  setIsPymentModalOpened: Dispatch<SetStateAction<boolean>>;
}

export function PaymentStep({
  paymentInUSD,
  preselectedMethod,
  setIsPaymentDialogOpen,
  referralCode,
  handleChangeStep,
  setActualPaymentAmount,
  actualPaymentAmount,
  setIsSucces,
  setIsPymentModalOpened,
}: SendDepositProps) {
  const form = useFormContext<PaymentFormType>();
  const { currentRound, currentBonus, currentReferralProgram } = appStore();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { address: connectedWalletAddress } = useAccount();

  if (!form) {
    throw new Error("This component must be used within a <FormProvider />");
  }

  const orderId = form.watch("orderId");
  const paymentMethod = form.watch("paymentMethod");
  const paymentStatus = form.watch("paymentStatus");
  const address = form.watch("payAddress");
  const payedCoinsValue = form.watch("value");
  const referralCodeInForm = form.watch("referralCode");

  const tokens = calcAmountOfTokensToShow(
    paymentInUSD,
    currentRound,
    referralCode,
  );

  const toensWithoutBonus = calcAmountOfTokensToShow(
    paymentInUSD,
    currentRound,
    false,
  );

  const [copiedText, copyToClipboard] = useCopyToClipboard(2000);
  const handleCopy = () => {
    copyToClipboard(address);
  };

  useEffect(() => {
    if (!orderId) return;

    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const pollingAction = async () => {
      try {
        const order = await tokensApi.getOrderById(orderId);
        if (!order) return;

        const isFinalStatus =
          order.status === "EXPIRED" || order.status === "PAID";

        if (order.status === "EXPIRED") {
          toast.error(t("Transaction expired"));
          form.setValue("value", "");
          setIsPaymentDialogOpen(false);
        } else if (order.status === "PAID") {
          toast.success(
            t("You have successfully purchased {{amount}} tokens", {
              amount: order.amount.toFixed(2),
            }),
          );

          if (referralCodeInForm) {
            localStorage.setItem("referralCode", referralCodeInForm);
          }
          setActualPaymentAmount(order.amount);
          setIsSucces(true);
          setIsPymentModalOpened(true);
          await tokensApi.getCurrentRound();
          await authApi.getUser();
          tokensApi.getCurrentReferralProgram();
          tokensApi.getProgress();
          tokensApi.getCurrentBonus();
          handleChangeStep(Step.Completed);
        } else {
          form.setValue("paymentStatus", order.status);
        }
        if (!isFinalStatus) {
          timeoutId = setTimeout(pollingAction, 3000);
        }
      } catch (error) {
        console.log(error);
        toast.error(t("Transaction expired"));
      }
    };

    pollingAction();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [orderId]);

  const referralBonusToShow =
    currentReferralProgram?.refereeBonusPercentAmount && tokens
      ? (Number(tokens) - Number(toensWithoutBonus)).toFixed(2)
      : "0";

  return (
    <>
      <div className="flex flex-col max-lg:gap-[8px] relative !overflow-hidden max-lg:rounded-[16px] max-lg:before:!rounded-[16px] rounded-[16px] before:!rounded-[16px] bg-[#111111] p-[12px] max-lg:p-[12px] pb-[12px] w-full shadow-[0px_6px_7.5px_0px_#0000000F] max-lg:shadow-[0px_6px_7.5px_0px_#0000000F]">
        <div
          className={clsx(
            "flex items-center justify-between w-full rounded-[16px] max-lg:rounded-[12px] mb-[20px] before:!rounded-[16px] max-lg:before:!rounded-[12px] p-[8px] max-lg:p-[8px]",
            gradientRowBgClass,
          )}
        >
          <div className="flex items-center gap-[16px] max-lg:gap-[12px]">
            <button
              className={clsx(
                "w-[31px] h-[31px] max-lg:w-[31px] max-lg:h-[31px] bg-[#252525] text-white hover:scale-[1.1] cursor-pinter rounded-full flex items-center justify-center flex-shrink-0",
                shadowAndBorderClass,
              )}
              onClick={() => setIsPaymentDialogOpen(false)}
            >
              <ChevronBack className="max-lg:w-[23px] max-lg:h-[23px]" />
            </button>
            <h2 className="text-[20px] max-lg:text-16m font-luxora-medium leading-[110%] text-white">
              {t("Payment Confirmation")}
            </h2>
          </div>

          <div
            className={clsx(
              "w-[88px] max-lg:w-[88px] h-[31px] max-lg:h-[31px] flex items-center justify-center rounded-[8px] max-lg:rounded-[8px] before:!rounded-[8px] max-lg:before:!rounded-[8px] bg-[#252525]",
              shadowAndBorderClass,
            )}
          >
            <div className="text-[16px] max-lg:text-[16px] text-white">
              {" "}
              Step 2/3
            </div>
          </div>
        </div>

        <div className="flex bg-[#1a1a1a] items-center justify-between border-[2px] rounded-[16px] max-lg:rounded-[12px] max-lg:p-[4px] max-lg:pr-[16px] lg:py-[8px] lg:pl-[8px] lg:pr-[16px] border-[#252525]">
          {" "}
          <div className=" max-lg:rounded-[8px] max-lg:h-[32px] h-[32px] rounded-[8px] flex items-center gap-[8px]">
            {" "}
            <ConnectWallet className="max-lg:w-[21px] max-lg:h-[21px]" />
            <div className="max-lg:pl-[4px] text-[14px] max-lg:text-14m font-luxora-regular text-left leading-[110%] text-white/40">
              {t("Wallet that receives $ATTS")}:
            </div>
          </div>
          <div
            className="text-[16px] max-lg:text-16m font-luxora-regular leading-normal text-white truncate max-lg:max-w-[188px] max-w-[288px]"
            title={connectedWalletAddress}
          >
            {truncateMiddle(String(connectedWalletAddress), 3, 3)}
          </div>
        </div>

        <div className="flex flex-col gap-[4px] max-lg:gap-[4px] mt-[16px]">
          <div className="max-lg:pl-[4px] text-[14px] max-lg:text-14m font-luxora-regular text-left leading-[110%] text-white/40">
            {t("Send this exact amount")}
          </div>
          <div className="flex items-center justify-between border-[2px] rounded-[16px] max-lg:rounded-[12px] max-lg:p-[4px] max-lg:pr-[16px] lg:py-[8px] lg:pl-[8px] lg:pr-[16px] border-[#252525]">
            <div className="flex items-center gap-2">
              {" "}
              <img
                src={paymentMethod.icon}
                alt={paymentMethod.name}
                className="w-[20px] h-[20px] max-lg:w-[32px] max-lg:h-[32px]"
              />
              <div className="text-[16px] max-lg:text-16m font-luxora-regular leading-normal text-white">
                {paymentMethod.currency}
              </div>
            </div>
            <div className="text-[14px] max-lg:text-16m font-luxora-regular text-white">
              {payedCoinsValue || 0}
            </div>
          </div>
        </div>
        <div
          className={clsx(
            "flex justify-center py-[32px] max-lg:py-[24px] rounded-[16px] max-lg:rounded-[12px] max-lg:before:!rounded-[12px] before:!rounded-[16px] bg-[#111111] flex-col items-center my-[16px]",
            shadowAndBorderClass,
          )}
        >
          <QRCode
            value={address}
            size={178}
            removeQrCodeBehindLogo
            bgColor="#111111"
            fgColor="#FFFFFF"
          />
          <p className="text-[14px] max-lg:text-14m font-luxora-regular lg:w-[178px] leading-[110%] text-center text-white mt-[16px] max-lg:mt-[12px] ">
            {t("Scan this QR Code to send")} <br /> {paymentMethod.networkTOSow}{" "}
            {t("tokens to this wallet")}
          </p>
        </div>
        {isLoading && (
          <div className="mb-[7px] max-lg:mb-[4px]">
            {" "}
            <TransactionStatusChecker />
          </div>
        )}

        <div
          className={clsx(
            "bg-[#1a1a1a] border border-white/10 rounded-[12px] max-lg:rounded-[12px]",
            "flex items-center justify-center p-[6px] max-lg:p-[6px] mb-[12px] max-lg:mb-[8px]",
          )}
        >
          <span className="text-[14px] flex gap-[6px] items-center max-lg:text-14m leading-[1.2] text-white font-luxora-medium font-normal">
            <img
              src={networkData[paymentMethod.network as NetworkKey].icon}
              alt=""
              className="w-[10px] max-lg:w-[10px] h-[10px] max-lg:h-[10px]"
            />
            {t("networkFilter.networkOnly", {
              networkName: paymentMethod.networkTOSow,
            })}
          </span>
        </div>
        <div
          className={clsx(
            "flex items-center rounded-[12px] max-lg:rounded-[12px] max-lg:before:!rounded-[12px] gap-[8px] max-lg:gap-[4px] max-lg:!p-[8px] max-lg:!pr-[19px] bg-[#1a1a1a] pr-[4px] pl-[16px] py-[8px] mb-[12px] max-lg:mb-[8px]",
            shadowAndBorderClass,
          )}
        >
          <span className="flex-grow w-0 text-left max-lg:text-14m text-[14px] leading-[1.2] text-white/40 font-mono break-all">
            {address}
          </span>
          <div className="relative">
            <Button
              className={referralCtaButtonClass}
              onClick={handleCopy}
              disabled={!!copiedText}
            >
              <span className="font-luxora-medium font-normal text-black text-[16px] max-lg:text-[16px] tracking-[0] leading-[110%] whitespace-nowrap">
                {copiedText ? t("Copied!") : t("Copy")}
              </span>
              <Copy
                className="w-[18px] h-[18px] max-lg:w-[18px] max-lg:h-[18px] shrink-0 text-black"
                aria-hidden
              />
            </Button>
          </div>
        </div>
        <p className="text-[14px] max-lg:text-12m leading-[1.2] text-center text-[#8F8F8F]">
          {t("Transaction confirmation can take up to 10 minutes")}
        </p>

        <div className="flex gap-[8px] mt-[16px]">
          <Button
            onClick={() => {
              setIsLoading(true);
            }}
            className={investButtonClass}
            disabled={isLoading}
          >
            <span className="text-black max-lg:text-16m text-[16px] leading-[110%] font-[500]">
              {" "}
              {t("heroForm.iHaveSentTokens")}
            </span>
            <div className="bg-[#F3F3F8] max-lg:rounded-[8px] max-lg:w-[32px] max-lg:h-[32px] w-[32px] h-[32px] rounded-[8px] flex items-center justify-center">
              {" "}
              <CheckmarkIcon className="size-[18px]" />
            </div>
          </Button>
        </div>
      </div>
    </>
  );
}
